import React from 'react';
import { Icon } from '../icons.jsx';
import {
  LizziLogo, Avatar, AvatarStack, StatusChip,
  Sidebar, UserMenu, MenuItem,
  Topbar, CommandPalette,
  ChatDock, ChatMessage, FormattedText, ChatBlock,
  ToastHost,
  ProgressBar, StatPill,
  fmtMoney, fmtPct, fmtDate, fmtDateFull,
} from '../components.jsx';
import { NewBidModal, UploadModal, NotificationsPanel, notify } from '../modals.jsx';
import { MockData } from '../mock/index.js';

const { useState, useEffect, useRef, useMemo, useCallback } = React;

// ===============================================
// Calendario · timeline + Gantt
// ===============================================
function CalendarScreen() {
  const { CALENDAR, BIDS } = window.MockData;
  const [view, setView] = useState('timeline');

  return (
    <div className="viewport-inner fade-in">
      <div className="page-head">
        <div>
          <div className="page-eyebrow">Plazos · 9 hitos próximos</div>
          <h1 className="page-title">Calendario</h1>
          <p className="muted" style={{fontSize:14, marginTop:2, maxWidth:580}}>
            Plazos críticos, comités, revisiones internas y entregas a PLACE. Todo sincronizado con el estado de cada licitación.
          </p>
        </div>
        <div style={{display:'flex', gap:8}}>
          <div style={{display:'flex', border:'1px solid var(--line-2)', borderRadius:8, overflow:'hidden'}}>
            <button className="btn btn-ghost" style={{padding:'6px 10px', background: view==='timeline'?'var(--bg-2)':'transparent', fontSize:12.5}} onClick={()=>setView('timeline')}>Timeline</button>
            <button className="btn btn-ghost" style={{padding:'6px 10px', background: view==='gantt'?'var(--bg-2)':'transparent', fontSize:12.5}} onClick={()=>setView('gantt')}>Gantt</button>
          </div>
          <button className="btn btn-primary" onClick={()=>notify('Crear hito en el calendario', 'Plus')}><Icon.Plus size={14}/> Nuevo hito</button>
        </div>
      </div>

      {view === 'timeline' && <Timeline/>}
      {view === 'gantt' && <GanttView/>}
    </div>
  );
}

function Timeline() {
  const { CALENDAR } = window.MockData;
  const grouped = CALENDAR.reduce((acc, e) => {
    const day = e.date;
    (acc[day] = acc[day] || []).push(e);
    return acc;
  }, {});
  const days = Object.keys(grouped).sort();
  return (
    <div className="card" style={{padding:'12px 22px'}}>
      {days.map(d => (
        <div key={d} style={{display:'grid', gridTemplateColumns:'130px 1fr', gap:24, padding:'18px 0', borderBottom:'1px solid var(--line-1)'}}>
          <div>
            <div className="font-display" style={{fontSize:38, lineHeight:1}}>{new Date(d).getDate()}</div>
            <div style={{fontSize:12, color:'var(--text-3)', textTransform:'uppercase', marginTop:2, letterSpacing:'0.05em'}}>{new Date(d).toLocaleDateString('es-ES',{month:'long', weekday:'long'})}</div>
          </div>
          <div style={{display:'flex', flexDirection:'column', gap:8}}>
            {grouped[d].map(e => {
              const bid = window.MockData.BIDS.find(b=>b.id===e.bid);
              const tone = { milestone:'sem-info', meeting:'sem-neutral', deadline:'sem-warning', submit:'sem-danger' }[e.type];
              const labels = { milestone:'Hito', meeting:'Reunión', deadline:'Plazo', submit:'Presentación' };
              return (
                <div key={e.id} style={{display:'grid', gridTemplateColumns:'80px 100px 1fr auto', gap:14, alignItems:'center', padding:'10px 12px', background:'var(--bg-2)', borderRadius:8}}>
                  <span className="font-mono" style={{fontSize:12, color:'var(--text-3)'}}>{e.time}</span>
                  <span className={`sem ${tone}`} style={{fontSize:11}}>{labels[e.type]}</span>
                  <div>
                    <div style={{fontSize:13.5}}>{e.title}</div>
                    <div style={{fontSize:11.5, color:'var(--text-4)', marginTop:2}}>{bid?.entityShort}</div>
                  </div>
                  <Avatar user={window.MockData.USERS.find(u=>u.id===e.owner)} size={22}/>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function GanttView() {
  // Build a simple Gantt for SANDETEL execution (12 months) — illustrative
  const months = ['Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun'];
  const rows = [
    { phase:1, label:'1 · Activación y arranque',       start:0,  end:1,  owner:'Director/a' },
    { phase:1, label:'1.1 Plan de trabajo inicial',     start:0,  end:1,  owner:'Director/a', child:true },
    { phase:1, label:'1.2 Equipo y onboarding',          start:0,  end:2,  owner:'RRHH',       child:true },
    { phase:2, label:'2 · Captación de startups',       start:1,  end:5,  owner:'Coordinador' },
    { phase:2, label:'2.1 Convocatoria nacional',        start:1,  end:3,  owner:'Comunicación', child:true },
    { phase:2, label:'2.2 Evaluación y selección',       start:3,  end:5,  owner:'Consultor sr.', child:true },
    { phase:3, label:'3 · Aceleración (4 cohortes)',    start:2,  end:11, owner:'Consultor sr.' },
    { phase:3, label:'3.1 Cohorte 1',                    start:2,  end:5,  owner:'Coordinador', child:true },
    { phase:3, label:'3.2 Cohorte 2',                    start:4,  end:7,  owner:'Coordinador', child:true },
    { phase:3, label:'3.3 Cohorte 3',                    start:6,  end:9,  owner:'Coordinador', child:true },
    { phase:3, label:'3.4 Cohorte 4',                    start:8,  end:11, owner:'Coordinador', child:true },
    { phase:4, label:'4 · Cierre y evaluación',         start:10, end:12, owner:'Director/a' },
    { phase:4, label:'4.1 Memoria final',                start:11, end:12, owner:'Director/a', child:true },
  ];
  return (
    <div className="card" style={{overflow:'hidden'}}>
      <div style={{display:'grid', gridTemplateColumns:'220px 1fr', borderBottom:'1px solid var(--line-1)', background:'var(--bg-2)'}}>
        <div style={{padding:'10px 14px', fontSize:11, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.05em'}}>Actividad</div>
        <div style={{display:'grid', gridTemplateColumns:'repeat(12, 1fr)', borderLeft:'1px solid var(--line-1)'}}>
          {months.map((m,i) => (
            <div key={i} style={{padding:'10px 6px', fontSize:11, color:'var(--text-3)', textAlign:'center', borderRight:'1px solid var(--line-1)', fontFamily:'JetBrains Mono'}}>{m}</div>
          ))}
        </div>
      </div>
      <div className="gantt">
        {rows.map((r,i) => (
          <div key={i} className="gantt-row">
            <div className="gantt-label" style={{paddingLeft: r.child ? 28 : 14, color: r.child ? 'var(--text-3)' : 'var(--text-1)', fontWeight: r.child ? 400 : 500}}>{r.label}</div>
            <div className="gantt-track" style={{borderLeft:'1px solid var(--line-1)', display:'grid', gridTemplateColumns:'repeat(12, 1fr)'}}>
              {months.map((_,j) => (
                <div key={j} style={{borderRight:'1px solid var(--line-1)'}}></div>
              ))}
              <div className={`gantt-bar phase-${r.phase}`} style={{
                left: (r.start/12*100)+'%',
                width: ((r.end - r.start)/12*100)+'%',
              }}></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

window.CalendarScreen = CalendarScreen;


export { CalendarScreen, Timeline, GanttView };
